import { Lock } from '@/components/icons'
import { SubscribeModal } from '@/components/modal'
import { useProfileViews } from '@/hooks/useProfileViews'
import { useUpgradeCta } from '@/hooks/useUpgradeCta'
import { useTranslation } from 'react-i18next'

type BadgeProps = {
  viewsRemaining: number
}

/** Lock hero with the remaining-views counter (0 at the limit, like mobile). */
const GateLockBadge = ({ viewsRemaining }: BadgeProps) => {
  return (
    <div className="relative">
      <div className="w-16 h-16 rounded-full bg-primary-500 bg-opacity-10 flex items-center justify-center">
        <Lock className="w-7 h-7 text-primary-500" />
      </div>
      <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-navy-900 flex items-center justify-center text-sm font-bold text-white">
        {viewsRemaining}
      </div>
    </div>
  )
}

/** At the limit the gate is a pure DUPR+ upsell. */
const GateLimitReached = ({ onUpgrade }: { onUpgrade: () => void }) => {
  const { t } = useTranslation()
  return (
    <>
      <h2 className="text-xl font-semibold text-navy-900">{t('player.profileUnlock.limitReached')}</h2>
      <p className="text-sm text-navy-900 text-opacity-70">{t('player.profileUnlock.limitReachedSubtitle')}</p>
      <button
        className="mt-2 rounded-full bg-primary-500 px-8 py-2.5 text-sm font-semibold text-white hover:bg-opacity-70"
        onClick={onUpgrade}
      >
        {t('player.profileUnlock.upgrade')}
      </button>
    </>
  )
}

type SpendProps = {
  onUnlock: () => void
  onUpgrade: () => void
}

/** Within the allotment the gate offers to spend a free view. */
const GateSpendView = ({ onUnlock, onUpgrade }: SpendProps) => {
  const { t } = useTranslation()
  return (
    <>
      <h2 className="text-xl font-semibold text-navy-900">{t('player.profileUnlock.title')}</h2>
      <p className="text-sm text-navy-900 text-opacity-70">{t('player.profileUnlock.subtitle')}</p>
      <button
        className="mt-2 rounded-full bg-primary-500 px-8 py-2.5 text-sm font-semibold text-white hover:bg-opacity-70"
        onClick={onUnlock}
      >
        {t('player.profileUnlock.unlock')}
      </button>
      <button className="text-sm font-medium text-primary-500 underline hover:opacity-70" onClick={onUpgrade}>
        {t('player.profileUnlock.upgradeLink')}
      </button>
    </>
  )
}

type Props = {
  /** Spends one profile view on the gated profile. */
  onUnlock: () => void
}

/**
 * Full-page gate shown in place of another player's profile when the viewer is
 * profile-view limited and has not unlocked this profile. Spends a view, or
 * upsells DUPR+ once the allotment is gone. Styled as the app's standard white
 * card so it sits naturally on the page's blue-gradient background.
 */
export const ProfileUnlockGate = ({ onUnlock }: Props) => {
  const { viewsRemaining, viewsLimitReached } = useProfileViews()
  const { openUpgrade, subscribeOpen, closeSubscribe } = useUpgradeCta('profile_limit_cta_clicked')

  return (
    <div className="flex w-full justify-center py-10">
      <div className="w-full max-w-md flex flex-col items-center gap-y-4 rounded-3xl bg-white px-6 py-10 text-center shadow-xl">
        <GateLockBadge viewsRemaining={viewsRemaining} />
        {viewsLimitReached ? (
          <GateLimitReached onUpgrade={openUpgrade} />
        ) : (
          <GateSpendView onUnlock={onUnlock} onUpgrade={openUpgrade} />
        )}
      </div>
      <SubscribeModal open={subscribeOpen} onClose={closeSubscribe} />
    </div>
  )
}
